import { ReactComponent as ConfirmationNumberSVG } from '@/asset/svg/confirmationNumber.svg'
import Button from '@/component/Button'
import Input from '@/component/Input'
import TGASDK from '@/config/TGASDK'
import type { GrantType } from '@kulee/tga-sdk/types/types/uc'
import type { InputProps } from 'rc-input'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const VerificationCodeInput: React.FC<PropType> = ({ email, grantType, maxLength = 6, ...rest }) => {
  const { t } = useTranslation('uc')
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const handleSendCode = async () => {
    if (!email || countdown > 0) {
      return
    }

    setLoading(true)
    await TGASDK.module.UC.service.sendEmailCode({
      email,
      grantType
    })
    // 发送成功后开始倒计时
    setCountdown(60)
    setLoading(false)
  }

  // 处理倒计时
  useEffect(() => {
    if (countdown === 0) {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return
    }

    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)

    // 清理
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [countdown])

  const prefix = (
    <span className="flex items-center justify-center">
      <ConfirmationNumberSVG className="w-6 fill-fontcolor-500" />
    </span>
  )

  const suffix = (
    <Button type="link" size="small" loading={loading} onClick={handleSendCode} disabled={!email || countdown > 0}>
      {countdown > 0 ? t('tip.getCodeAgain', { count: countdown }) : t('btn.getCode')}
    </Button>
  )

  return (
    <Input
      {...rest}
      autoComplete="one-time-code"
      inputMode="numeric"
      maxLength={maxLength}
      allowClear={true}
      prefix={prefix}
      suffix={suffix}
    />
  )
}

type PropType = InputProps & {
  email?: string
  grantType: GrantType
}

export default VerificationCodeInput
